import React from 'react';
import { Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import type { WearableActivitySummary } from '@front-activerse/common-components';
import type { ApolloError } from '@apollo/client';
import type { DateRange } from '../types';
import { toChartSeries } from '../helpers/series';
import MetricValue from './MetricValue';
import MetricChart from './MetricChart';
import ProviderDeviceChip from './ProviderDeviceChip';

interface ActivityMetric {
  key: keyof WearableActivitySummary;
  label: string;
  unit: string;
}

const ACTIVITY_METRICS: ActivityMetric[] = [
  {
    key: 'steps',
    label: 'Steps',
    unit: 'steps',
  },
  {
    key: 'distanceMeters',
    label: 'Distance',
    unit: 'm',
  },
  {
    key: 'floorsClimbed',
    label: 'Floors climbed',
    unit: 'floors',
  },
  {
    key: 'elevationMeters',
    label: 'Elevation',
    unit: 'm',
  },
  {
    key: 'activeCaloriesKcal',
    label: 'Active calories',
    unit: 'kcal',
  },
  {
    key: 'totalCaloriesKcal',
    label: 'Total calories',
    unit: 'kcal',
  },
  {
    key: 'activeMinutes',
    label: 'Active minutes',
    unit: 'min',
  },
  {
    key: 'sedentaryMinutes',
    label: 'Sedentary minutes',
    unit: 'min',
  },
  {
    key: 'lightIntensityMinutes',
    label: 'Light intensity minutes',
    unit: 'min',
  },
  {
    key: 'moderateIntensityMinutes',
    label: 'Moderate intensity minutes',
    unit: 'min',
  },
  {
    key: 'vigorousIntensityMinutes',
    label: 'Vigorous intensity minutes',
    unit: 'min',
  },
  {
    key: 'heartRateAvgBpm',
    label: 'Heart rate (avg)',
    unit: 'bpm',
  },
  {
    key: 'heartRateMaxBpm',
    label: 'Heart rate (max)',
    unit: 'bpm',
  },
  {
    key: 'heartRateMinBpm',
    label: 'Heart rate (min)',
    unit: 'bpm',
  },
];

interface Props {
  range: DateRange;
  loading: boolean;
  error: ApolloError | undefined;
  activitySummary: WearableActivitySummary[];
}

const ActivitySummarySection: React.FC<Props> = ({ range, loading, error, activitySummary }) => {
  const dayRow = activitySummary[activitySummary.length - 1] ?? null;

  return (
    <Card className="mb-4" data-testid="activity-summary-section">
      <CardHeader>Activity</CardHeader>
      <CardBody>
        {loading && !dayRow ? <Spinner /> : null}
        {error ? (
          <div data-testid="activity-summary-error" className="text-danger">
            Could not load section: {error.message}
          </div>
        ) : null}
        {!loading && !error && activitySummary.length === 0 ? (
          <div data-testid="activity-summary-empty">
            {range.isSingleDay ? 'No data for the selected day' : 'No data for selected period'}
          </div>
        ) : null}
        {range.isSingleDay && dayRow
          ? ACTIVITY_METRICS.map((metric) => (
              <MetricValue
                key={metric.key as string}
                label={metric.label}
                unit={metric.unit}
                value={dayRow[metric.key] as number | null}
                provider={dayRow.provider}
                device={dayRow.device}
              />
            ))
          : null}
        {!range.isSingleDay && activitySummary.length > 0 ? (
          <>
            {(() => {
              const lastWithProvider = [...activitySummary].reverse().find((row) => row.provider);
              return lastWithProvider ? (
                <div className="mb-3">
                  <ProviderDeviceChip
                    provider={lastWithProvider.provider}
                    device={lastWithProvider.device}
                  />
                </div>
              ) : null;
            })()}
            {ACTIVITY_METRICS.map((metric) => (
              <div key={metric.key as string} className="mb-4">
                <small className="text-muted d-block mb-1">{metric.label}</small>
                <MetricChart
                  points={toChartSeries(
                    activitySummary,
                    range,
                    (row) => row[metric.key] as number | null
                  )}
                  ariaLabel={metric.label}
                  unit={metric.unit}
                />
              </div>
            ))}
          </>
        ) : null}
      </CardBody>
    </Card>
  );
};

export default ActivitySummarySection;
export { ACTIVITY_METRICS };
